/**
 * The zipline network, and a figure riding it as the page scrolls.
 *
 * Ziplines are how you actually cross the Cursed Apple, so the backdrop gets
 * the cables and someone on them. Scrolling carries the rider down the line;
 * scrolling back carries them up. It is the reader's own movement being drawn,
 * not a loop playing beside it.
 *
 * The cable draws itself just ahead of the rider rather than being laid out in
 * full. Showing the entire route at once gave away the shape of the page below
 * the fold — you could see where the line went before you got there.
 *
 * Cables and rider live in one component because they are one system: they
 * share the anchor geometry AND the reveal position, and computing that
 * scroll measurement twice in two components is exactly how the two halves
 * would drift out of alignment.
 *
 * Position is derived from where this box sits relative to the viewport, NOT
 * from scroll progress. Progress was the obvious approach and it was wrong:
 * this box is shorter than the scrollable document and starts below the nav,
 * so progress 0.5 is not half way down the cables, which parked the rider a
 * few hundred pixels above the viewport for most of the page. Measuring the
 * box is self-correcting — it cannot go out of sync with the layout.
 */

import { useEffect, useRef } from "react";
import {
  motion,
  useAnimationFrame,
  useMotionTemplate,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useSpring,
  useTransform,
} from "framer-motion";

import riderSrc from "../assets/pictures/zip-rider.png";
import riderFastSrc from "../assets/pictures/zip-rider-fast.png";

/**
 * Anchor pylons, as percentages of the box. The y values ascend, which is what
 * lets a height identify one point on the line (see pointAtY).
 *
 * The network used to open at [4, 3], and that cable ran from the top-left
 * corner down through the hero headline — the one diagonal that met the
 * reading column head-on. It was replaced by the [80, 13] stub, which stays
 * in the right gutter and starts behind the hero artwork, so the line emerges
 * from under it instead of beginning at a point hanging in mid-air.
 */
export const ZIP_ANCHORS: [number, number][] = [
  [80, 13],
  [95, 16],
  [6, 33],
  [93, 49],
  [8, 66],
  [96, 82],
  [5, 97],
];

const SEGMENTS = ZIP_ANCHORS.length - 1;

/* Two sprites, 72x76 each: hanging, and stretched out flat at speed. In game a
   hero grabs the line with one raised fist rather than a trolley, so the
   contact point is that fist.

   Both were rendered onto a shared canvas at one scale with their fists on the
   same pixel, which is what lets them cross-fade in place. Fitted to their own
   bounding boxes they would differ in size and grip position, and the character
   would jump sideways every time the boost came in. The calm pose is narrow, so
   it carries transparent padding on its left. */
const RIDER_W = 72;
const RIDER_H = 76;
const GRIP_X = 0.9517;

/* Raw scroll speed, in px/sec, at which the rider starts to stretch out and at
   which the boost is fully in.

   Measured from the scroll position rather than from the rider's spring. The
   spring deliberately damps velocity, which squeezed a relaxed read-scroll
   (~30%/s) and a hard flick (~44%/s) so close together that gentle scrolling
   already tripped the boost most of the way. Raw scroll speed separates them
   cleanly, and is what "scrolling fast" actually means to the reader. */
const BOOST_FROM_PX = 550;
const BOOST_FULL_PX = 2400;

/* Asymmetric smoothing: snap up to a new speed, ease back down from it. A
   flick is over in a few frames, so the effect has to outlast the input to
   register at all — but only just. Released too slowly it stops reading as a
   burst of speed and starts looking like a state the rider is stuck in. */
const SPEED_ATTACK = 0.55;
const SPEED_RELEASE = 0.06;

/** Length of the speed streak drawn along the cable behind the rider, in px. */
const TRAIL_PX = 280;

/** Height in the viewport the rider rides at, as a fraction. */
const VIEW_ANCHOR = 0.42;

/* The rider is absent in the hero and fades in as you scroll out of it.
   At rest it lands at 42% of the viewport, which is exactly where the hero
   headline sits — so on first paint the character was hanging across "THE
   UNFAIR ADVANTAGE". Moving the band would only trade that collision for
   another against the sub-copy or the buttons, so it stays off until the
   headline is on its way out. In fractions of viewport height, to track the
   hero, which is sized in vh. */
const FADE_IN_START = 0.25;
const FADE_IN_END = 0.8;

/* How far ahead of the rider the cable is drawn, in the same percentage units
   as the anchors. Enough that there is visibly track ahead to travel along,
   not so much that the route below the fold is given away. */
const REVEAL_LEAD = 5;

/* Below this speed the rider holds its current facing. Scroll velocity crosses
   zero constantly at rest and at the end of a flick, and reading the sign
   directly would make the character twitch back and forth while nobody is
   moving. In percent of the box height per second. */
const TURN_AROUND_SPEED = 0.4;

/* A rider dangles from their own arm under gravity, so the body stays close to
   vertical and only leans into the line — it does not lie flat along it. Taking
   a fraction of the cable angle reads as momentum; taking all of it looks like
   a decal stuck to the cable. */
const LEAN = 0.4;

/** Fraction of a segment, at each end, spent swinging round the pylon. */
const TURN = 0.06;

const Y_MIN = ZIP_ANCHORS[0][1];
const Y_MAX = ZIP_ANCHORS[ZIP_ANCHORS.length - 1][1];

const BLOOM = "#2dd4bf";
const CORE = "#c8fff5";

/* Taut beams of light, not sagging wire: a hot white-cyan core inside a soft
   cyan bloom. Three stacked strokes give a cleaner falloff than a blur filter
   and cost nothing. Kept faint — this is a backdrop, and at full strength the
   cables competed with the reading column. */
const CABLE_LAYERS = [
  { color: BLOOM, opacity: 0.04, width: 7 },
  { color: BLOOM, opacity: 0.1, width: 2.5 },
  { color: CORE, opacity: 0.22, width: 0.9 },
];

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/**
 * Invert the cable polyline by height. The anchors descend monotonically, so a
 * y uniquely identifies one point on the line — which means x is *derived* from
 * y rather than tracked alongside it, and the rider is on the cable by
 * construction rather than by two values happening to agree.
 */
function pointAtY(y: number) {
  for (let i = 0; i < SEGMENTS; i++) {
    const [x1, y1] = ZIP_ANCHORS[i];
    const [x2, y2] = ZIP_ANCHORS[i + 1];
    if (y <= y2 || i === SEGMENTS - 1) {
      const f = clamp((y - y1) / (y2 - y1), 0, 1);
      return { x: lerp(x1, x2, f), seg: i, f };
    }
  }
  return { x: ZIP_ANCHORS[0][0], seg: 0, f: 0 };
}

export default function Zipline() {
  const still = useReducedMotion() ?? false;
  const boxRef = useRef<HTMLDivElement>(null);
  const lastTop = useRef(Number.NaN);
  const segRefs = useRef<(SVGGElement | null)[]>([]);

  /* Lean and cable lengths are only meaningful in pixels: the page is roughly
     three times taller than it is wide, so a segment that looks gentle in
     percentage space is steep on screen. Kept in a ref and refreshed from the
     same measurement the position uses, so it survives resize without a
     re-render. */
  const aspect = useRef({ w: 1440, h: 4500 });

  /* Reveal only ever advances. Retracting the cable when the reader scrolls
     back up would unbuild the world behind them, which reads as a glitch
     rather than an effect. */
  const revealPeak = useRef(Y_MIN);

  /** +1 riding down the line, -1 riding back up it. */
  const travelDir = useRef(1);

  /* Raw scroll velocity tracking, smoothed with a running average so one
     stalled frame does not read as a flick. */
  const lastScrollY = useRef(0);
  const lastTime = useRef(0);
  const scrollSpeed = useRef(0);

  const yPct = useMotionValue(Y_MIN);
  const ySmooth = useSpring(yPct, {
    /* Critically damped (damping = 2*sqrt(stiffness*mass)): settles in about a
       fifth of a second with no wobble, so the rider trails the scroll slightly
       instead of being welded to it. Overdamping this — easy to do by accident
       with a small mass — makes the rider crawl and lag off-screen. */
    stiffness: 550,
    damping: 47,
    mass: 1,
  });

  const xPct = useMotionValue(ZIP_ANCHORS[0][0]);
  const rotate = useMotionValue(0);
  const face = useMotionValue(1);
  const fade = useMotionValue(0);

  /* Boost: how hard the reader is scrolling, 0..1. Sprung so the pose eases in
     and out instead of snapping between the two sprites on every wheel tick. */
  const boostRaw = useMotionValue(0);
  const boost = useSpring(boostRaw, { stiffness: 320, damping: 30, mass: 1 });
  const calmOpacity = useTransform(boost, (b) => (1 - b) * 0.6);
  /* The calm pose sits at 0.6 so it stays background. The boost may come up
     brighter and grow slightly, since at 0.6 and unscaled it was too faint to
     notice at 76px tall — but only slightly. It is an accent on a movement the
     reader is already making, not a set piece. */
  const fastOpacity = useTransform(boost, (b) => b * 0.95);
  const boostScale = useTransform(boost, (b) => 1 + b * 0.06);
  const trailOpacity = useTransform(boost, (b) => b * 0.9);

  const trailPaths = useRef<(SVGPathElement | null)[]>([]);

  /**
   * Streak of light along the cable, trailing the rider. Drawn as a window cut
   * into the segment with a dash pattern — one dash the length of the streak,
   * then a gap longer than the whole cable so the pattern never repeats.
   *
   * Which side of the rider it sits on follows the direction of travel, so
   * riding back up the page throws the streak below them rather than leaving
   * it stuck out in front.
   */
  const applyTrail = (seg: number, f: number) => {
    const { w, h } = aspect.current;
    const [x1, y1] = ZIP_ANCHORS[seg];
    const [x2, y2] = ZIP_ANCHORS[seg + 1];
    const len = Math.hypot(((x2 - x1) / 100) * w, ((y2 - y1) / 100) * h);
    const at = len * f;
    const start = travelDir.current > 0 ? Math.max(0, at - TRAIL_PX) : at;
    const end = travelDir.current > 0 ? at : Math.min(len, at + TRAIL_PX);
    const d = `M ${x1} ${y1} L ${x2} ${y2}`;
    for (const p of trailPaths.current) {
      if (!p) continue;
      p.setAttribute("d", d);
      p.style.strokeDasharray = `${Math.max(0, end - start)} ${len + TRAIL_PX + 10}`;
      p.style.strokeDashoffset = String(-start);
    }
  };

  /**
   * Draw each cable up to the reveal height. Lengths are in screen pixels
   * rather than normalised via pathLength, because these strokes use
   * non-scaling-stroke — dashes are then measured in screen space, and mixing
   * that with a user-space pathLength is asking for browser disagreement.
   */
  const applyReveal = (revealY: number) => {
    const { w, h } = aspect.current;
    for (let i = 0; i < SEGMENTS; i++) {
      const g = segRefs.current[i];
      if (!g) continue;
      const [x1, y1] = ZIP_ANCHORS[i];
      const [x2, y2] = ZIP_ANCHORS[i + 1];
      const len = Math.hypot(((x2 - x1) / 100) * w, ((y2 - y1) / 100) * h);
      const f = clamp((revealY - y1) / (y2 - y1), 0, 1);
      // dasharray and dashoffset are inherited, so setting them on the group
      // covers all three stacked strokes at once
      g.style.strokeDasharray = String(len);
      g.style.strokeDashoffset = String(len * (1 - f));
    }
  };

  /* Where the box sits in the document, measured only when the layout can have
     changed: on mount, on resize, and when the box or the page above it changes
     size. The frame loop used to call getBoundingClientRect() on every frame,
     which forces a layout pass sixty times a second for the whole visit. Its
     on-screen top is the document top less the scroll offset, which is exact
     for an element in normal flow. */
  const geom = useRef<{ top: number; w: number; h: number } | null>(null);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      geom.current = { top: r.top + window.scrollY, w: r.width, h: r.height };
      lastTop.current = Number.NaN;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    ro.observe(document.body);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  useAnimationFrame(() => {
    const g = geom.current;
    if (!g || g.h <= 0) return;
    const r = { top: g.top - window.scrollY, width: g.w, height: g.h };

    /* Scroll speed is measured on EVERY frame, before the bail-out below.
       Behind that bail-out it never ran once the page stopped moving, so the
       speed never decayed and the rider stayed stretched out in the boost pose
       indefinitely after a single flick. */
    const now = performance.now();
    const sy = window.scrollY;
    const dt = (now - lastTime.current) / 1000;
    if (dt > 0 && dt < 0.25) {
      const instant = Math.abs(sy - lastScrollY.current) / dt;
      const k = instant > scrollSpeed.current ? SPEED_ATTACK : SPEED_RELEASE;
      scrollSpeed.current += (instant - scrollSpeed.current) * k;
      if (!still) {
        boostRaw.set(
          clamp(
            (scrollSpeed.current - BOOST_FROM_PX) / (BOOST_FULL_PX - BOOST_FROM_PX),
            0,
            1,
          ),
        );
      }
    }
    lastTime.current = now;
    lastScrollY.current = sy;

    // position work only when the box actually moved
    if (r.top === lastTop.current) return;
    lastTop.current = r.top;

    aspect.current = { w: r.width, h: r.height };
    const vh = window.innerHeight;
    const target = vh * VIEW_ANCHOR;
    yPct.set(clamp(((target - r.top) / r.height) * 100, Y_MIN, Y_MAX));

    /* Also drive the reveal from here, not only from the spring's change
       event. At the top of the page the spring is already at rest at Y_MIN, so
       it never emits a change and the cables stayed at their hidden initial
       dashoffset — the hero's opening stub was invisible until the reader
       scrolled. This runs on the first frame, so the network is drawn up to
       the lead distance before anyone touches the wheel. */
    if (!still) {
      revealPeak.current = Math.max(revealPeak.current, ySmooth.get() + REVEAL_LEAD);
      applyReveal(revealPeak.current);
    }

    const scrolled = window.scrollY;
    fade.set(
      clamp(
        (scrolled - vh * FADE_IN_START) / (vh * (FADE_IN_END - FADE_IN_START)),
        0,
        1,
      ),
    );
  });

  useMotionValueEvent(ySmooth, "change", (y) => {
    const { x, seg, f } = pointAtY(clamp(y, Y_MIN, Y_MAX));
    xPct.set(x);

    revealPeak.current = Math.max(revealPeak.current, y + REVEAL_LEAD);
    applyReveal(revealPeak.current);

    const { w, h } = aspect.current;
    const legFor = (i: number) => {
      const [x1, y1] = ZIP_ANCHORS[i];
      const [x2, y2] = ZIP_ANCHORS[i + 1];
      const dx = ((x2 - x1) / 100) * w;
      const dy = ((y2 - y1) / 100) * h;
      const theta = (Math.atan2(dy, dx) * 180) / Math.PI;
      /* Travelling left the sprite is mirrored first and rotated after, so the
         cable angle has to be brought back into the near-vertical half before
         it is applied, or the rider ends up upside down. */
      return { rot: (dx > 0 ? theta : theta - 180) * LEAN, face: dx > 0 ? 1 : -1 };
    };

    const here = legFor(seg);

    /* Scrolling back up rides the line in reverse, so the rider turns to face
       the way they are actually going. Reversing along a straight cable is a
       180-degree turn, and mirroring the sprite already is that turn — so only
       the facing flips here and the lean angle is left alone. Deriving the
       rotation again for the reverse case gives the same number back. */
    const v = ySmooth.getVelocity();
    if (v > TURN_AROUND_SPEED) travelDir.current = 1;
    else if (v < -TURN_AROUND_SPEED) travelDir.current = -1;

    applyTrail(seg, f);

    /* Near a pylon, blend into the next leg's lean and facing. scaleX passing
       through zero on its way from +1 to -1 reads as the rider swinging round
       the anchor, which is exactly what is happening. */
    let blendedFace: number;
    if (f < TURN && seg > 0) {
      const prev = legFor(seg - 1);
      const t = 0.5 + f / (2 * TURN);
      rotate.set(lerp(prev.rot, here.rot, t));
      blendedFace = lerp(prev.face, here.face, t);
    } else if (f > 1 - TURN && seg < SEGMENTS - 1) {
      const next = legFor(seg + 1);
      const t = (f - (1 - TURN)) / (2 * TURN);
      rotate.set(lerp(here.rot, next.rot, t));
      blendedFace = lerp(here.face, next.face, t);
    } else {
      rotate.set(here.rot);
      blendedFace = here.face;
    }
    face.set(blendedFace * travelDir.current);
  });

  const left = useMotionTemplate`${xPct}%`;
  const top = useMotionTemplate`${ySmooth}%`;

  return (
    <div
      ref={boxRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
    >
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full opacity-60"
        fill="none"
      >
        {/* Strokes only, no dots or nodes. This svg is stretched over the whole
            page with preserveAspectRatio="none", which scales x and y by wildly
            different factors — so anything round here is not round. The anchor
            nodes that used to sit at each pylon were r="1.6" and painted as
            47x142px vertical ellipses, reading as stray blobs down the left
            edge. Paths survive that stretch; circles cannot. */}
        {ZIP_ANCHORS.slice(0, -1).map(([x1, y1], i) => {
          const [x2, y2] = ZIP_ANCHORS[i + 1];
          const d = `M ${x1} ${y1} L ${x2} ${y2}`; // taut, no sag
          return (
            <g
              key={i}
              ref={(el) => {
                segRefs.current[i] = el;
              }}
              /* Start fully hidden so nothing flashes at full length on the
                 first paint, before the first reveal lands. Reduced motion
                 skips the draw-on and shows the finished network. */
              style={still ? undefined : { strokeDasharray: 1, strokeDashoffset: 1 }}
            >
              {CABLE_LAYERS.map((l, j) => (
                <path
                  key={j}
                  d={d}
                  stroke={l.color}
                  strokeOpacity={l.opacity}
                  strokeWidth={l.width}
                  vectorEffect="non-scaling-stroke"
                  strokeLinecap="round"
                />
              ))}
            </g>
          );
        })}

        {/* Speed streak: a hot run of cable trailing the rider, lit only while
            the reader is scrolling hard. Sits above the cables so it reads as
            the line itself flaring rather than a separate object. */}
        {!still && (
          <motion.g style={{ opacity: trailOpacity }}>
            <path
              ref={(el) => {
                trailPaths.current[0] = el;
              }}
              stroke={BLOOM}
              strokeOpacity="0.42"
              strokeWidth="10"
              vectorEffect="non-scaling-stroke"
              strokeLinecap="round"
            />
            <path
              ref={(el) => {
                trailPaths.current[1] = el;
              }}
              stroke={CORE}
              strokeOpacity="0.9"
              strokeWidth="2.2"
              vectorEffect="non-scaling-stroke"
              strokeLinecap="round"
            />
          </motion.g>
        )}
      </svg>

      {!still && (
        <motion.div
          className="absolute hidden md:block"
          style={{
            left,
            top,
            rotate,
            opacity: fade,
            // the raised fist is both the point that rides the cable and the
            // point the body swings about, so pull it onto the anchor and
            // pivot there
            transformOrigin: `${GRIP_X * 100}% 0%`,
            marginLeft: -GRIP_X * RIDER_W,
            width: RIDER_W,
            height: RIDER_H,
          }}
        >
          <motion.div
            className="relative h-full w-full"
            style={{
              scaleX: face,
              scale: boostScale,
              /* Mirror about the fist, not the sprite's middle. Flipping about
                 the middle would throw the grip sideways and leave the rider
                 hanging in mid-air beside the cable on every leftward leg.
                 Applied to the wrapper so both poses mirror together. */
              transformOrigin: `${GRIP_X * 100}% 50%`,
            }}
          >
            {/* Two poses stacked in the same box, cross-faded by scroll speed.
                Their fists sit on the same pixel, so the swap happens in place
                rather than the character hopping sideways. */}
            <motion.img
              src={riderSrc}
              alt=""
              draggable={false}
              className="absolute inset-0 h-full w-full select-none"
              style={{
                /* Deliberately faint. The rider always paints behind the
                   content, but cards are only half-opaque, so a bright figure
                   bleeds through and reads as sitting on top in some places and
                   under it in others. Dim keeps the show-through uniform. */
                filter: "drop-shadow(0 0 6px rgba(45,212,191,0.3))",
                opacity: calmOpacity,
              }}
            />
            <motion.img
              src={riderFastSrc}
              alt=""
              draggable={false}
              className="absolute inset-0 h-full w-full select-none"
              style={{
                // brighter and hotter, since this pose only shows at speed
                filter: "drop-shadow(0 0 14px rgba(45,212,191,0.75))",
                opacity: fastOpacity,
              }}
            />
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
